import type { FunctionComponent } from 'react';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { ChildcareAccordionTable } from '../../../components/ChildcareAccordionTable/ChildcareAccordionTable';
import { ChildcareCentre } from '../../../components/ChildcareAccordionTable/types';
import { mapApiBadgeVariantToBadgeVariant } from '../../../app-kit/helpers/mapApiBadgeVariantToBadgeVariant';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import { ResultOf } from 'gql.tada';
import { propertyPageChildcareCollectionFragment } from '../../graphql/propertyPageQuery';
import { NearbyChildcareCentresBrick } from '../../../components/NearbyChildcareCentresBrick/NearbyChildcareCentresBrick';

type ApiChildcareCentre = NonNullable<
  NonNullable<
    ResultOf<typeof propertyPageChildcareCollectionFragment>['nearbyChildcareCentres']
  >['centres']
>[number];

const mapApiChildcareCentreToChildcareCentreProp = (
  childcareCentre: ApiChildcareCentre,
): ChildcareCentre => {
  return {
    isDefaultOpen: childcareCentre.isDefaultOpen,
    tracking: childcareCentre.tracking,
    ...childcareCentre.centre,
    vacancyBadge: {
      text: childcareCentre.centre.vacancyBadge.text,
      variant: mapApiBadgeVariantToBadgeVariant(childcareCentre.centre.vacancyBadge.variant),
    },
    webLinks: childcareCentre.centre.webLinks
      ? childcareCentre.centre.webLinks.map((webLink) => ({
          ...webLink,
          variant: mapApiWeblinkVariantToLinkVariant(webLink.variant),
        }))
      : [],
  };
};

export const NearbyChildcareCentresBrickWithData: FunctionComponent = () => {
  const { displayModal } = usePropertyPageModalContext();
  const data = usePropertyPageQuery()?.data.propertyPageBySlugV3;
  if (!data?.nearbyChildcareCentres || !data.nearbyChildcareCentres.centres) return null;

  const { title, subtitle, centreFactHeader, centres } = data.nearbyChildcareCentres;
  return (
    <NearbyChildcareCentresBrick
      title={title}
      subtitle={subtitle}
      pageSource="property_page"
      displayModal={displayModal}
    >
      <ChildcareAccordionTable
        childcareFactHeader={centreFactHeader}
        childcareCentres={centres.map(mapApiChildcareCentreToChildcareCentreProp)}
      />
    </NearbyChildcareCentresBrick>
  );
};
